import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import { PLATFORM_LANGS, type LocalizedText, type PlatformLang } from '../model/types';

/** Um campo por idioma, com a sigla na frente. */
export function LocalizedFields({
  id,
  label,
  value,
  onChange,
  multiline = false,
}: {
  id: string;
  label: string;
  value: LocalizedText;
  onChange: (next: LocalizedText) => void;
  multiline?: boolean;
}) {
  const set = (lang: PlatformLang, text: string) => onChange({ ...value, [lang]: text });
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="space-y-1.5">
        {PLATFORM_LANGS.map((lang) => (
          <div key={lang.value} className="flex items-start gap-2">
            <span className="mt-2.5 w-7 shrink-0 font-mono text-[11px] uppercase text-muted-foreground">{lang.label}</span>
            {multiline ? (
              <Textarea id={`${id}-${lang.value}`} rows={2} value={value[lang.value]} onChange={(e) => set(lang.value, e.target.value)} />
            ) : (
              <Input id={`${id}-${lang.value}`} value={value[lang.value]} onChange={(e) => set(lang.value, e.target.value)} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Variáveis disponíveis; clicar copia `{{nome}}`. */
export function VariableChips({ variables }: { variables: string[] }) {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (name: string) => {
    const token = `{{${name}}}`;
    try {
      await navigator.clipboard.writeText(token);
      setCopied(name);
      setTimeout(() => setCopied((current) => (current === name ? null : current)), 1200);
    } catch {
      /* sem clipboard: o texto já está visível no chip */
    }
  };
  return (
    <div className="flex flex-wrap gap-1.5">
      {variables.map((name) => (
        <button
          key={name}
          type="button"
          onClick={() => void copy(name)}
          className={cn(
            'inline-flex items-center gap-1 rounded-md border border-border/60 bg-muted/40 px-2 py-0.5 font-mono text-[11px] transition-colors hover:border-primary/40 hover:bg-primary/[0.06]',
          )}
          aria-label={`Copiar {{${name}}}`}
        >
          {`{{${name}}}`}
          {copied === name ? <Check className="size-3 text-success" /> : <Copy className="size-3 text-muted-foreground" />}
        </button>
      ))}
    </div>
  );
}
